<script setup lang="ts">
import CopyLink from '@/components/CopyLink.vue'

import type { Partner } from '@/types/types'

import MarketPlaceOauthTokenList from './MarketPlaceOauthTokenList.vue'

const props = defineProps<{
  account?: any
  partner?: Partner
  isProcessing?: boolean
}>()
const { t } = useI18n()

const isSyncProcessing = ref(props.isProcessing)
const customersSyncing = ref(props.account.currentCustomersSyncs.length > 0)

watch(
  () => props.isProcessing,
  () => (isSyncProcessing.value = props.isProcessing)
)

function handleCustomersSync() {
  emit('handleSyncRequested', props.account)
}

const emit = defineEmits<{
  (e: 'handleSyncRequested', account: any): void
}>()
</script>
<template>
  <div class="mb-2 space-y-6 rounded-md bg-muted p-4">
    <p class="mb-2 block text-sm font-medium text-muted-foreground">
      {{ t('pages.marketplace.marketplacePage.form.generalDetails.title') }}
    </p>
    <div class="mb-4">
      <label>
        {{ t('pages.marketplace.tabletsPage.form.authentication.accountDate') }}
      </label>
      <div class="mt-1 w-full rounded-md border bg-white p-3">
        {{ new Date(account?.createdAt).toLocaleDateString('fr-FR') }}
      </div>
    </div>
    <div v-if="account?.merchantIdFromPartner && partner.isMerchantAssociationRequired" class="mb-4">
      <label>
        {{ t('pages.marketplace.tabletsPage.form.authentication.merchantIdFromPartner') }}
      </label>
      <CopyLink :link="account?.merchantIdFromPartner" text="global.words.copy" class="mt-1 w-full" />
    </div>
    <div v-if="account?.apiUrl && partner.hasCustomApiUrl" class="mb-4">
      <label>
        {{ t('pages.marketplace.tabletsPage.form.authentication.websiteLink') }}
      </label>
      <CopyLink :link="account?.apiUrl" text="global.words.copy" class="mt-1 w-full" />
    </div>
    <div
      v-if="
        account?.apiUsername &&
        (partner.partnerAuthenticationType?.slug === 'basic-auth' ||
          partner.partnerAuthenticationType?.slug === 'password')
      "
      class="mb-4"
    >
      <label>
        {{
          partner?.partnerText?.api_username_field_label ??
          (partner.partnerAuthenticationType?.slug === 'basic-auth'
            ? t('pages.marketplace.marketplacePage.form.authentication.apiKey')
            : t(`pages.marketplace.marketplacePage.apiUsername`))
        }}
      </label>
      <CopyLink :link="account?.apiUsername" text="global.words.copy" class="mt-1 w-full" />
    </div>
    <div v-if="account?.apiToken && partner.partnerAuthenticationType?.slug === 'token'" class="mb-4">
      <label>
        {{ t(`pages.marketplace.marketplacePage.apiToken`) }}
      </label>
      <CopyLink :link="account?.apiToken" text="global.words.copy" class="mt-1 w-full" />
    </div>
    <div v-if="partner?.isUsingOpenApi">
      <label class="mb-1">
        {{ t('pages.marketplace.marketplacePage.openApi.connectedUsers') }}
      </label>
      <MarketPlaceOauthTokenList :partner="partner" />
    </div>
    <div v-if="partner?.partnerAuthenticationType?.slug === 'internal'">
      <div class="mb-4">
        <label>Registration URL</label>
        <CopyLink :link="partner?.authenticationUrl" text="global.words.copy" class="mt-1 w-full" />
      </div>
      <div class="mb-4">
        <label>API key</label>
        <CopyLink :link="account?.apiToken" text="global.words.copy" class="mt-1 w-full" />
      </div>
    </div>
  </div>
  <div v-if="partner.features.customersSync == true" class="space-y-6 rounded-md bg-muted p-4">
    <div v-if="!customersSyncing && !isSyncProcessing" class="flex flex-row items-center justify-between gap-x-2">
      <label class="hidden md:block">
        {{ t('integrations.customerSyncing.label') }}
      </label>
      <Button variant="outline" class="text-center align-middle md:flex" @click="handleCustomersSync">
        <BaseIcon name="HeroArrowPathIconSolid" class="h-5 w-5" />
        <p class="text-sm">{{ t('integrations.customerSyncing.button.sync') }}</p>
      </Button>
    </div>
    <div v-else>
      {{ t('integrations.customerSyncing.progress') }}
    </div>
  </div>
</template>
